import React, { useState } from "react";
import { ShieldCheckIcon, TruckIcon, LifeBuoyIcon, ClockIcon, AwardIcon, WalletIcon, MapPinIcon, SparklesIcon, LanguagesIcon, StarIcon, PackageIcon, LayoutGridIcon, PlusIcon, BoxIcon } from "lucide-react";
import { StepHeading, AiButton } from "../SettingsUI";
import { ShopConfig, SuggestedQuestion, ShopHighlight, ShopLanguage, FeaturedMode, HIGHLIGHT_ICONS, AVAILABLE_LANGUAGES } from "../../../data/shopConfig";
import { fleetVehicles } from "../../../data/easylease";
import { DesignSection, ControlLabel, Segmented, AddRow } from "../design/DesignControls";
import { ItemRow } from "../design/ItemRow";
const ICON_MAP: Record<string, React.ElementType> = {
  shield: ShieldCheckIcon,
  truck: TruckIcon,
  support: LifeBuoyIcon,
  clock: ClockIcon,
  award: AwardIcon,
  wallet: WalletIcon,
  map: MapPinIcon,
  sparkles: SparklesIcon
};
const QUESTION_IDEAS = ['Do you offer delivery?', 'Compare Prado vs Land Cruiser', 'I need a 7-seater for one month', 'Show me electric or hybrid options', 'What is included in the insurance?', 'Do you have anything available today?'];
const HIGHLIGHT_IDEAS: Omit<ShopHighlight, 'id'>[] = [{
  icon: 'wallet',
  title: 'No Hidden Fees',
  detail: 'Transparent pricing on every booking'
}, {
  icon: 'award',
  title: 'Newest Models',
  detail: 'Fleet refreshed every 12 months'
}, {
  icon: 'map',
  title: 'Dubai & Abu Dhabi',
  detail: 'Branches across both emirates'
}];
type SectionId = 'languages' | 'questions' | 'highlights' | 'featured' | 'categories';
interface Props {
  config: ShopConfig;
  update: (patch: Partial<ShopConfig>) => void;
  /** Rendered inside the Shop Studio panel — single column, no big header. */
  compact?: boolean;
}
function move<T>(list: T[], index: number, direction: -1 | 1): T[] {
  const next = [...list];
  const target = index + direction;
  if (target < 0 || target >= list.length) return list;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}
const inputCls = 'w-full px-3.5 py-2.5 bg-white ring-1 ring-slate-200 rounded-xl text-sm text-dark-navy outline-none focus:ring-2 focus:ring-primary-indigo/40 transition-shadow duration-150 ease-out';
export function ShopContent({
  config,
  update,
  compact
}: Props) {
  const [open, setOpen] = useState<SectionId | null>(null);
  const [editingQ, setEditingQ] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [editingH, setEditingH] = useState<ShopHighlight | null>(null);
  const [picking, setPicking] = useState(false);
  const [addingLang, setAddingLang] = useState(false);
  const [lang, setLang] = useState(config.defaultLanguage);
  const content = config.content;
  const toggle = (id: SectionId) => setOpen(o => o === id ? null : id);
  const setContent = (patch: Partial<ShopConfig['content']>) => update({
    content: {
      ...content,
      ...patch
    }
  });
  const enabledLanguages = config.languages.map(code => AVAILABLE_LANGUAGES.find(l => l.code === code)).filter((l): l is ShopLanguage => Boolean(l));
  const addable = AVAILABLE_LANGUAGES.filter(l => !config.languages.includes(l.code));
  const isDefaultLang = lang === config.defaultLanguage;
  const activeLanguage = enabledLanguages.find(l => l.code === lang);
  const defaultName = enabledLanguages.find(l => l.code === config.defaultLanguage)?.name;
  const rtl = !isDefaultLang && activeLanguage?.rtl ? 'rtl' : undefined;
  const tr = (key: string) => content.i18n[lang]?.[key] ?? '';
  const writeI18n = (entries: Record<string, string>) => setContent({
    i18n: {
      ...content.i18n,
      [lang]: {
        ...(content.i18n[lang] ?? {}),
        ...entries
      }
    }
  });
  const chosen = content.featuredIds.map(id => fleetVehicles.find(v => v.id === id)).filter((v): v is (typeof fleetVehicles)[number] => Boolean(v));
  const available = fleetVehicles.filter(v => !content.featuredIds.includes(v.id));
  const visibleCats = content.categories.filter(c => c.visible).length;
  const suggestQuestions = () => {
    const existing = content.questions.map(q => q.text);
    const additions = QUESTION_IDEAS.filter(q => !existing.includes(q)).slice(0, 3);
    setContent({
      questions: [...content.questions, ...additions.map((text, i) => ({
        id: `q${Date.now()}${i}`,
        text
      }))]
    });
  };
  const suggestHighlights = () => {
    const existing = content.highlights.map(h => h.title);
    const additions = HIGHLIGHT_IDEAS.filter(h => !existing.includes(h.title)).slice(0, 2);
    setContent({
      highlights: [...content.highlights, ...additions.map((h, i) => ({
        ...h,
        id: `h${Date.now()}${i}`
      }))]
    });
  };
  const commitQuestion = (q: SuggestedQuestion) => {
    if (isDefaultLang) {
      const text = draft.trim() || q.text;
      setContent({
        // A new question left blank is simply discarded.
        questions: text ? content.questions.map(x => x.id === q.id ? {
          ...x,
          text
        } : x) : content.questions.filter(x => x.id !== q.id)
      });
    } else {
      writeI18n({
        [q.id]: draft
      });
    }
    setEditingQ(null);
  };
  const saveHighlight = () => {
    if (!editingH) return;
    if (!isDefaultLang) {
      writeI18n({
        [`${editingH.id}.title`]: editingH.title,
        [`${editingH.id}.detail`]: editingH.detail
      });
    } else {
      const exists = content.highlights.some(x => x.id === editingH.id);
      setContent({
        highlights: exists ? content.highlights.map(x => x.id === editingH.id ? editingH : x) : [...content.highlights, editingH]
      });
    }
    setEditingH(null);
  };
  return <div className="space-y-3" data-id="element-4031" data-mp="2stm0lm26z7xn">
      {compact ? <p className="text-[13px] text-slate-500 leading-relaxed pb-1" data-id="element-4032" data-mp="2xsyng6t2cf08">
          Choose what customers see first on your shop.
        </p> : <StepHeading title="Shop Content" desc="Choose what customers see first on your shop." data-id="element-4033" data-mp="3lhrm4b12l0yj" />}

      {/* Which language is being written — affects questions and highlights */}
      {enabledLanguages.length > 1 && <div className="rounded-2xl bg-slate-50 p-3" data-id="element-4034" data-mp="3yspx9t7820g">
          <p className="text-[12px] text-slate-500 mb-2" data-id="element-4035" data-mp="3v6ojyr4vkdnj">Writing in</p>
          <Segmented<string> label="Language you are writing in" value={lang} onChange={setLang} options={enabledLanguages.map(l => ({
        id: l.code,
        label: l.nativeName
      }))} data-id="element-4036" data-mp="3v4c0uandrx8w" />
          {!isDefaultLang && <p className="text-[12px] text-slate-500 mt-2 leading-relaxed" data-id="element-4037" data-mp="3h6nh5651uw5f">
              Anything you leave blank shows in {defaultName}.
            </p>}
        </div>}

      {/* Questions */}
      <DesignSection icon={BoxIcon} title="Suggested questions" summary={`${content.questions.length} shown under your chat box`} open={open === 'questions'} onToggle={() => toggle('questions')} data-id="element-4038" data-mp="3ce2paa5pet4j">
        <div className="flex items-center justify-between gap-3" data-id="element-4039" data-mp="32fwpby3gjx2b">
          <p className="text-[12.5px] text-slate-500" data-id="element-4040" data-mp="2xi4zd9mdil0r">Tap one to edit it.</p>
          {isDefaultLang && <AiButton onClick={suggestQuestions} label="Suggest" data-id="element-4041" data-mp="2ticmikpy5spl" />}
        </div>
        <div className="space-y-2 !mt-3" data-id="element-4042" data-mp="18zscl2bl1rh5">
          {content.questions.map((q, i) => editingQ === q.id ? <input key={q.id} autoFocus dir={rtl} value={draft} onChange={e => setDraft(e.target.value)} onBlur={() => commitQuestion(q)} onKeyDown={e => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') setEditingQ(null);
        }} className={inputCls} data-id="element-4043" data-mp="1czui5mkfv51p" /> : <ItemRow key={q.id} label={q.text} onEdit={() => {
          setDraft(isDefaultLang ? q.text : tr(q.id));
          setEditingQ(q.id);
        }} onUp={i > 0 ? () => setContent({
          questions: move(content.questions, i, -1)
        }) : undefined} onDown={i < content.questions.length - 1 ? () => setContent({
          questions: move(content.questions, i, 1)
        }) : undefined} onRemove={isDefaultLang ? () => setContent({
          questions: content.questions.filter(x => x.id !== q.id)
        }) : undefined} data-id="element-4044" data-mp="pbt45y4hqwew">
                {isDefaultLang ? <span className="block text-[13.5px] text-dark-navy truncate" data-id="element-4045" data-mp="311otlzd89bsr">{q.text}</span> : <>
                    <span dir={rtl} className={`block text-[13.5px] truncate ${tr(q.id) ? 'text-dark-navy' : 'text-slate-400 italic'}`} data-id="element-4046" data-mp="2w2lo6dtc53k9">
                      {tr(q.id) || 'Tap to translate'}
                    </span>
                    <span className="block text-[11.5px] text-slate-400 truncate" data-id="element-4047" data-mp="352sa8o33nbv1">{q.text}</span>
                  </>}
              </ItemRow>)}
          {isDefaultLang && <AddRow label="Add question" onClick={() => {
          const q: SuggestedQuestion = {
            id: `q${Date.now()}`,
            text: ''
          };
          setContent({
            questions: [...content.questions, q]
          });
          setDraft('');
          setEditingQ(q.id);
        }} data-id="element-4048" data-mp="3jypxthyyj7vn" />}
        </div>
      </DesignSection>

      {/* Highlights */}
      <DesignSection icon={StarIcon} title="Why customers choose you" summary={content.highlights.length ? content.highlights.map(h => h.title).join(' · ') : 'None added'} open={open === 'highlights'} onToggle={() => toggle('highlights')} data-id="element-4049" data-mp="6mft68xffi1i">
        <div className="flex items-center justify-between gap-3" data-id="element-4050" data-mp="q7g3ootpmbrp">
          <p className="text-[12.5px] text-slate-500" data-id="element-4051" data-mp="q6y1lbkd93bv">Short reasons shown as a row on your shop.</p>
          {isDefaultLang && <AiButton onClick={suggestHighlights} label="Suggest" data-id="element-4052" data-mp="bc9w3xeyu4w9" />}
        </div>
        <div className="space-y-2 !mt-3" data-id="element-4053" data-mp="g7pb4sopm6u9">
          {content.highlights.map((h, i) => {
          const Icon = ICON_MAP[h.icon] ?? ShieldCheckIcon;
          const title = isDefaultLang ? h.title : tr(`${h.id}.title`);
          return <ItemRow key={h.id} label={h.title} lead={<span className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0" data-id="element-4055" data-mp="3opv79e1btgl7">
                    <Icon className="w-4 h-4 text-slate-600" strokeWidth={1.75} data-id="element-4056" data-mp="3toycozl7xotp" />
                  </span>} onEdit={() => setEditingH(isDefaultLang ? h : {
            ...h,
            title: tr(`${h.id}.title`),
            detail: tr(`${h.id}.detail`)
          })} onUp={i > 0 ? () => setContent({
            highlights: move(content.highlights, i, -1)
          }) : undefined} onDown={i < content.highlights.length - 1 ? () => setContent({
            highlights: move(content.highlights, i, 1)
          }) : undefined} onRemove={isDefaultLang ? () => setContent({
            highlights: content.highlights.filter(x => x.id !== h.id)
          }) : undefined} data-id="element-4054" data-mp="7aez3542idoa" data-mpi={i}>
                <span dir={rtl} className={`block text-[13.5px] font-semibold truncate ${title ? 'text-dark-navy' : 'text-slate-400 italic font-normal'}`} data-id="element-4057" data-mp="3v874nfs40gvd">
                  {title || 'Tap to translate'}
                </span>
                <span className="block text-[11.5px] text-slate-500 truncate" data-id="element-4058" data-mp="3mflyr09ku4ny">
                  {isDefaultLang ? h.detail : h.title}
                </span>
              </ItemRow>;
        })}

          {editingH ? <div className="p-3.5 rounded-xl bg-slate-50 space-y-3" data-id="element-4059" data-mp="i1drzxwhgrcv">
              {isDefaultLang && <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Icon" data-id="element-4060" data-mp="mtxexlh28yuc">
                  {HIGHLIGHT_ICONS.map(key => {
              const Icon = ICON_MAP[key];
              const on = editingH.icon === key;
              return <button key={key} role="radio" aria-checked={on} aria-label={key} onClick={() => setEditingH({
                ...editingH,
                icon: key
              })} className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors duration-150 ease-out ${on ? 'bg-dark-navy text-white' : 'bg-white text-slate-500 ring-1 ring-slate-200 hover:ring-slate-300'}`} data-id="element-4061" data-mp="22jy33jml2ymw">
                        <Icon className="w-4 h-4" strokeWidth={1.75} data-id="element-4062" data-mp="1jngdp8v7jatt" />
                      </button>;
            })}
                </div>}
              <input autoFocus dir={rtl} value={editingH.title} onChange={e => setEditingH({
            ...editingH,
            title: e.target.value
          })} placeholder="e.g. Fully insured" className={inputCls} data-id="element-4063" data-mp="1tjt7s7unzqp3" />
              <input dir={rtl} value={editingH.detail} onChange={e => setEditingH({
            ...editingH,
            detail: e.target.value
          })} placeholder="One short line (optional)" className={inputCls} data-id="element-4064" data-mp="34xn5x9tj0dgj" />
              <div className="flex justify-end gap-2" data-id="element-4065" data-mp="3gzeojxzxtddc">
                <button onClick={() => setEditingH(null)} className="px-3 py-2 text-[13px] font-semibold text-slate-500 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-4066" data-mp="3lzj85ww56o7s">
                  Cancel
                </button>
                <button onClick={saveHighlight} disabled={!editingH.title.trim()} className="px-4 py-2 rounded-lg bg-primary-indigo text-white text-[13px] font-semibold hover:bg-indigo-700 disabled:opacity-40 transition-colors duration-150 ease-out" data-id="element-4067" data-mp="2tcq2d03wayqx">
                  Save
                </button>
              </div>
            </div> : isDefaultLang && <AddRow label="Add a reason" onClick={() => setEditingH({
          id: `h${Date.now()}`,
          icon: 'shield',
          title: '',
          detail: ''
        })} data-id="element-4068" data-mp="3824h6nq8q8dp" />}
        </div>
      </DesignSection>

      {/* Featured products */}
      <DesignSection icon={PackageIcon} title="Featured products" summary={content.featuredMode === 'auto' ? 'Talab picks your popular items' : content.featuredMode === 'hidden' ? 'Hidden' : `${chosen.length} chosen by you`} open={open === 'featured'} onToggle={() => toggle('featured')} data-id="element-4069" data-mp="wnujom9gypex">
        <Segmented<FeaturedMode> label="Featured products" value={content.featuredMode} onChange={featuredMode => setContent({
        featuredMode
      })} options={[{
        id: 'auto',
        label: 'Talab picks'
      }, {
        id: 'manual',
        label: 'I choose'
      }, {
        id: 'hidden',
        label: 'Hide'
      }]} data-id="element-4070" data-mp="ipwcq1xh9v9g" />
        <p className="text-[12.5px] text-slate-500 !mt-2.5" data-id="element-4071" data-mp="4vltg0492tas">
          {content.featuredMode === 'auto' ? 'Your most popular items appear automatically.' : content.featuredMode === 'hidden' ? 'Your shop shows the chat only, with no product row.' : 'Pick the items to show, in the order you like.'}
        </p>

        {content.featuredMode === 'manual' && <div className="space-y-2 !mt-3" data-id="element-4072" data-mp="1q2f5h7nhifs2">
            {chosen.map((v, i) => <ItemRow key={v.id} label={v.name} lead={<img src={v.image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" data-id="element-4074" data-mp="1v0qfpjhoysat" />} onUp={i > 0 ? () => setContent({
          featuredIds: move(content.featuredIds, i, -1)
        }) : undefined} onDown={i < chosen.length - 1 ? () => setContent({
          featuredIds: move(content.featuredIds, i, 1)
        }) : undefined} onRemove={() => setContent({
          featuredIds: content.featuredIds.filter(id => id !== v.id)
        })} data-id="element-4073" data-mp="1v1s1ahwc2x6s" data-mpi={i}>
                <span className="block text-[13.5px] font-semibold text-dark-navy truncate" data-id="element-4075" data-mp="xe16opoknffg">{v.name}</span>
                <span className="block text-[11.5px] text-slate-500 truncate" data-id="element-4076" data-mp="12cujefw1b2ek">{v.monthly}</span>
              </ItemRow>)}
            {picking ? <div className="rounded-xl bg-slate-50 p-2" data-id="element-4077" data-mp="2rt3qq7uuq5az">
                <div className="flex items-center justify-between px-1.5 pb-2" data-id="element-4078" data-mp="2wr57un8pvujt">
                  <p className="text-[12px] text-slate-500" data-id="element-4079" data-mp="2wp15i9qgv3ln">Tap to add</p>
                  <button onClick={() => setPicking(false)} className="text-[12.5px] font-semibold text-primary-indigo" data-id="element-4080" data-mp="l4bhx6sopl1e">
                    Done
                  </button>
                </div>
                {available.length === 0 ? <p className="text-[12px] text-slate-400 px-1.5 pb-1" data-id="element-4081" data-mp="uvo9ryehgb7f">Everything is already featured.</p> : <div className="space-y-1 max-h-60 overflow-y-auto" data-id="element-4082" data-mp="q1k3zigye97x">
                    {available.map(v => <button key={v.id} onClick={() => setContent({
              featuredIds: [...content.featuredIds, v.id]
            })} className="w-full flex items-center gap-3 p-2 rounded-lg bg-white hover:ring-1 hover:ring-primary-indigo/40 text-left transition-shadow duration-150 ease-out" data-id="element-4083" data-mp="13sounv2cfr64">
                        <img src={v.image} alt="" className="w-8 h-8 rounded-md object-cover flex-shrink-0" data-id="element-4084" data-mp="18u5t7jxcv1jj" />
                        <span className="flex-1 min-w-0 text-[13px] font-medium text-dark-navy truncate" data-id="element-4085" data-mp="3flwv8vzdyyyu">{v.name}</span>
                        <PlusIcon className="w-4 h-4 text-primary-indigo flex-shrink-0" data-id="element-4086" data-mp="1ehkxzmtp1tks" />
                      </button>)}
                  </div>}
              </div> : <AddRow label="Add products" onClick={() => setPicking(true)} data-id="element-4087" data-mp="1jhh2roa7tp1e" />}
          </div>}
      </DesignSection>

      {/* Categories */}
      <DesignSection icon={LayoutGridIcon} title="Categories" summary={`${visibleCats} of ${content.categories.length} shown`} open={open === 'categories'} onToggle={() => toggle('categories')} data-id="element-4088" data-mp="1nkm3blaqjgne">
        <p className="text-[12.5px] text-slate-500" data-id="element-4089" data-mp="1xemfu88cjo65">
          These come from your catalogue. Switch off any you don’t want customers to see.
        </p>
        <div className="space-y-2 !mt-3" data-id="element-4090" data-mp="277bdo0az53j0">
          {content.categories.map((c, i) => <ItemRow key={c.name} label={c.name} muted={!c.visible} onUp={i > 0 ? () => setContent({
          categories: move(content.categories, i, -1)
        }) : undefined} onDown={i < content.categories.length - 1 ? () => setContent({
          categories: move(content.categories, i, 1)
        }) : undefined} trailing={<button type="button" role="switch" aria-checked={c.visible} aria-label={`Show ${c.name}`} onClick={() => setContent({
          categories: content.categories.map(x => x.name === c.name ? {
            ...x,
            visible: !x.visible
          } : x)
        })} className={`relative ml-1 w-9 h-5 rounded-full flex-shrink-0 transition-colors duration-150 ease-out ${c.visible ? 'bg-primary-indigo' : 'bg-slate-200'}`} data-id="element-4092" data-mp="2vazthosfxws2">
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-[left] duration-150 ease-out ${c.visible ? 'left-[18px]' : 'left-0.5'}`} data-id="element-4093" data-mp="3e2k7nlrtu2ly" />
                </button>} data-id="element-4091" data-mp="2b8dcwh4w5fkz" data-mpi={i}>
              <span className="block text-[13.5px] text-dark-navy truncate py-1" data-id="element-4094" data-mp="348jzfb190h90">{c.name}</span>
            </ItemRow>)}
        </div>
      </DesignSection>

      {/* Languages */}
      <DesignSection icon={LanguagesIcon} title="Languages" summary={enabledLanguages.map(l => l.name).join(' · ')} open={open === 'languages'} onToggle={() => toggle('languages')} data-id="element-4095" data-mp="n4eozphrex5">
        <p className="text-[12.5px] text-slate-500" data-id="element-4096" data-mp="fi3hzinz7seb">Customers switch language from your shop’s top bar.</p>
        <div className="space-y-2 !mt-3" data-id="element-4097" data-mp="kadczd3y7ing">
          {enabledLanguages.map(l => {
          const isDefault = l.code === config.defaultLanguage;
          return <ItemRow key={l.code} label={l.name} onRemove={isDefault ? undefined : () => {
            update({
              languages: config.languages.filter(c => c !== l.code)
            });
            if (lang === l.code) setLang(config.defaultLanguage);
          }} data-id="element-4098" data-mp="ajitggi36x5p">
                <span className="flex items-center gap-2" data-id="element-4099" data-mp="1363k1eqqn587">
                  <span className="text-[13.5px] font-semibold text-dark-navy" data-id="element-4100" data-mp="oadsl12s9zw3">{l.name}</span>
                  <span className="text-[12px] text-slate-400" data-id="element-4101" data-mp="t98o93lunixm">{l.nativeName}</span>
                  <span className="ml-auto" data-id="element-4102" data-mp="3914ukgxs1ymv">
                    {isDefault ? <span className="text-[11px] font-semibold text-success" data-id="element-4103" data-mp="393r2wishsi65">Main language</span> : <span role="button" tabIndex={0} onClick={() => update({
                  defaultLanguage: l.code
                })} onKeyDown={e => e.key === 'Enter' && update({
                  defaultLanguage: l.code
                })} className="text-[11.5px] font-semibold text-slate-400 hover:text-primary-indigo cursor-pointer transition-colors duration-150 ease-out" data-id="element-4104" data-mp="3e1sjmx2g45eg">
                        Make main
                      </span>}
                  </span>
                </span>
              </ItemRow>;
        })}
          {addingLang ? <div className="rounded-xl bg-slate-50 p-3" data-id="element-4105" data-mp="18djqpoc2n3go">
              <div className="flex items-center justify-between mb-2" data-id="element-4106" data-mp="18f4nlkluyyhp">
                <ControlLabel data-id="element-4107" data-mp="1m5q6387eh6f2">Add a language</ControlLabel>
                <button onClick={() => setAddingLang(false)} className="text-[12.5px] font-semibold text-primary-indigo -mt-2.5" data-id="element-4108" data-mp="1hdzuz00n2eml">
                  Done
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-4109" data-mp="pmsbi65ekiud">
                {addable.map(l => <button key={l.code} onClick={() => update({
              languages: [...config.languages, l.code]
            })} className="px-3 py-1.5 rounded-full bg-white ring-1 ring-slate-200 text-[12.5px] font-medium text-slate-700 hover:ring-primary-indigo/50 transition-shadow duration-150 ease-out" data-id="element-4110" data-mp="pmjzshl0njkr">
                    {l.name}
                  </button>)}
                {addable.length === 0 && <p className="text-[12px] text-slate-400" data-id="element-4111" data-mp="3krseefwd5060">All available languages are added.</p>}
              </div>
            </div> : <AddRow label="Add language" onClick={() => setAddingLang(true)} data-id="element-4112" data-mp="3egawr4tv0af" />}
        </div>
      </DesignSection>
    </div>;
}